import { useState,useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'
import {vendorDashboardDetails } from '@/store/thunkMidddleware/usersMiddleware';
import routes from '@/routes/routes.json';

import { 
  ArrowLeft, 
  Crown, 
  Star, 
  MapPin, 
  Calendar, 
  Clock, 
  TrendingUp,
  Sparkles,
  BadgeCheck,
  Eye,
  Users,
  ChevronRight,
  Loader2
} from "lucide-react";
import { PageHeader } from '@/components/flup';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getVendorPromotions } from "@/store/thunkMidddleware/turfMiddleware";
import Images from "@/assets/images";
import VendorPayment from "./Payment/VedorPaymentHandler";
import PaymentProcessing from "@/pages/Payment/paymentProcessing";

import {  createRecommendation,verifyRecommendationSubscriptionPayment} from "@/store/thunkMidddleware/paymentMiddleware";
import { Values } from "@/constants/Values";
import { ButtonWithLoader } from "@/components/ui/buttonwithloader";
import SuccessBookingAnimation from "./Payment/SuccessBookingAnimation";
import { setShowPaymentSuccess } from "@/store/slices/global.Slice";
import { getDaysLeftFromTodayIST } from "@/lib/utils";

interface PromotedVenue {
  id: number;
  name: string;
  location: string;
  image: string;
  rating: number;
  reviews: number;
  plan: "basic" | "premium" | "elite";
  expiresAt: string;
  daysRemaining: number;
  views: number;
  bookings: number;
  sports: string[];
  isActive: boolean;
}

const promotedVenues: PromotedVenue[] = [
  {
    id: 1,
    name: "Champions Arena",
    location: "Koramangala, Bangalore",
    image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400",
    rating: 4.9,
    reviews: 328,
    plan: "elite",
    expiresAt: "2026-02-15",
    daysRemaining: 38,
    views: 12450,
    bookings: 89,
    sports: ["Football", "Cricket", "Basketball"],
    isActive: true,
  },
  {
    id: 2,
    name: "Sports Hub 360",
    location: "Indiranagar, Bangalore",
    image: "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=400",
    rating: 4.7,
    reviews: 256,
    plan: "premium",
    expiresAt: "2026-01-25",
    daysRemaining: 17,
    views: 8920,
    bookings: 67,
    sports: ["Badminton", "Tennis", "Squash"],
    isActive: true,
  },
  {
    id: 3,
    name: "Green Field Arena",
    location: "HSR Layout, Bangalore",
    image: "https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=400",
    rating: 4.8,
    reviews: 189,
    plan: "premium",
    expiresAt: "2026-01-20",
    daysRemaining: 12,
    views: 6540,
    bookings: 45,
    sports: ["Football", "Cricket"],
    isActive: true,
  },
  {
    id: 4,
    name: "Victory Sports Complex",
    location: "Whitefield, Bangalore",
    image: "https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?w=400",
    rating: 4.6,
    reviews: 145,
    plan: "basic",
    expiresAt: "2026-01-12",
    daysRemaining: 4,
    views: 3200,
    bookings: 23,
    sports: ["Basketball", "Volleyball"],
    isActive: true,
  },
  {
    id: 5,
    name: "Elite Sports Lounge",
    location: "JP Nagar, Bangalore",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400",
    rating: 4.5,
    reviews: 98,
    plan: "elite",
    expiresAt: "2026-03-01",
    daysRemaining: 52,
    views: 15600,
    bookings: 112,
    sports: ["Cricket", "Football", "Tennis", "Badminton"],
    isActive: true,
  },
];

interface NonPromotedVenue {
  id: number;
  name: string;
  location: string;
  image: string;
  rating: number;
  reviews: number;
  sports: string[];
}

const nonPromotedVenues: NonPromotedVenue[] = [
  {
    id: 101,
    name: "Urban Play Ground",
    location: "Electronic City, Bangalore",
    image: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=400",
    rating: 4.2,
    reviews: 67,
    sports: ["Football", "Cricket"],
  },
  {
    id: 102,
    name: "Sunset Sports Arena",
    location: "Marathahalli, Bangalore",
    image: "https://images.unsplash.com/photo-1544919982-382bf0f96d34?w=400",
    rating: 4.0,
    reviews: 45,
    sports: ["Badminton", "Table Tennis"],
  },
  {
    id: 103,
    name: "Metro Sports Club",
    location: "MG Road, Bangalore",
    image: "https://images.unsplash.com/photo-1562552052-c723e039a1ed?w=400",
    rating: 3.9,
    reviews: 32,
    sports: ["Tennis", "Squash"],
  },
  {
    id: 104,
    name: "City Athletic Center",
    location: "Jayanagar, Bangalore",
    image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400",
    rating: 4.1,
    reviews: 54,
    sports: ["Basketball", "Volleyball", "Gym"],
  },
];

const planConfig = {
  basic: {
    color: "bg-slate-500",
    gradient: "from-slate-400 to-slate-600",
    border: "border-slate-300",
    label: "Basic",
    icon: Star,
  },
  premium: {
    color: "bg-amber-500",
    gradient: "from-amber-400 to-orange-500",
    border: "border-amber-300",
    label: "Premium",
    icon: Crown,
  },
  elite: {
    color: "bg-flup-violet",
    gradient: "from-purple-500 via-pink-500 to-rose-500",
    border: "border-purple-300",
    label: "Elite",
    icon: Sparkles,
  },
};

const VenueRecommendation = () => {
  const navigate = useNavigate();
  const [venues] = useState<PromotedVenue[]>(promotedVenues);
  const dispatch = useAppDispatch()
  const venuesState = useAppSelector(state=>state.turf)
  const { vendorDashboardDetailsResponse} = useAppSelector((state) => state.profile);

  const activeVenues = venues.filter((v) => v.isActive);
  const totalViews = activeVenues.reduce((sum, v) => sum + v.views, 0);
  const totalBookings = activeVenues.reduce((sum, v) => sum + v.bookings, 0);
  const [isLoading,setIsLoading]= useState(false)
  const [initPayment,setInitPayment]= useState(false)
  const [selectedVenueId,setSelectedVenueId] = useState(null)

  const { showPaymentProcessing,showPaymentSuccess} = useAppSelector((state) => state.globalstate);

  const getExpiryStatus = (days: number) => {
    if (days <= 7) return { color: "text-red-500", bg: "bg-red-50", status: "Expiring Soon" };
    if (days <= 14) return { color: "text-amber-500", bg: "bg-amber-50", status: "Renew Soon" };
    return { color: "text-green-500", bg: "bg-green-50", status: "Active" };
  };


  useEffect(()=>{
    fetchVendorDBLVenuesSubscription()
  },[])

  const fetchVendorDBLVenuesSubscription=()=>{
    dispatch(getVendorPromotions({callback:(res)=>{
      console.log("getVendorPromotions response:",res?.payload)
      if(!vendorDashboardDetailsResponse.payload){
        fetchvendorDashboardDetails()

      }

    }}))
  }

  const fetchvendorDashboardDetails=()=>{
    dispatch(vendorDashboardDetails({callback:(res)=>{
      // console.log("vendorDashboardDetails:",res)
  
    }}))
  }

  

  const closeHandler=()=>{

  }
  const promoteVenueHandler=(venueId:string)=>{
    setInitPayment(true)
      setSelectedVenueId(venueId)
  }

  const renderPaymentProcessing=()=>{
    return showPaymentProcessing && <PaymentProcessing />
  }

  const renderPaymentSucessCard=()=>{
    return showPaymentSuccess && <SuccessBookingAnimation
        onClose={()=>{
          fetchVendorDBLVenuesSubscription()
          dispatch(setShowPaymentSuccess(false))
        }}
        show={true}
    />
  }


  const RenewNowHandler=(venueId:string)=>{
    setSelectedVenueId(venueId)
    setInitPayment(true)

  }
  return (
    <>
    {renderPaymentProcessing()}
    {renderPaymentSucessCard()}
    {}
        {initPayment && selectedVenueId && <VendorPayment
      backDropcloseHandler={closeHandler}
      apiCreateOrder={createRecommendation}
      apiVerifyPayment={verifyRecommendationSubscriptionPayment}
      onClose={()=>{
        console.log("Payment gateway closed!")
        setInitPayment(false)
        setSelectedVenueId(null)
        setIsLoading(false)

      }}
      onSuccess={(paymentdata)=>{
        console.log("payment succesfully captured:",paymentdata)
        setInitPayment(false)
        setSelectedVenueId(null)
        setIsLoading(false)
      }}
      purchaseType={Values.purchaseType.venueRecomendation}
      metadata={{
        venueId:selectedVenueId
      }}
    />}
   
    <div className="space-y-4">
      <PageHeader
        title="Promote venue"
        subtitle="Paid placement that puts a venue in front of more players."
      />

      {/* Stats Overview */}
      <section>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-flup-surface rounded-flup-lg p-5 border border-flup-hairline transition-colors hover:border-flup-hairline-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <BadgeCheck className="w-6 h-6 text-white" />
              </div>
              <div>
              {vendorDashboardDetailsResponse.isLoading || !vendorDashboardDetailsResponse?.payload ? 
                                <Loader2 className="animate-spin w-10 h-10 text-flup-violet" />:
                <p className="text-2xl font-bold text-flup-ink">{vendorDashboardDetailsResponse?.payload?.activeVenues || 0}</p>}
                <p className="text-sm text-flup-ink-3">Active Venues</p>
              </div>
            </div>
          </div>

          {/* <div className="bg-flup-surface rounded-2xl p-5 shadow-sm border border-flup-hairline hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: "0.1s" }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-flup-ink">{(totalViews / 1000).toFixed(1)}K</p>
                <p className="text-sm text-flup-ink-3">Total Views</p>
              </div>
            </div>
          </div> */}

          <div className="bg-flup-surface rounded-2xl p-5 shadow-sm border border-flup-hairline hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                {vendorDashboardDetailsResponse.isLoading || !vendorDashboardDetailsResponse?.payload ? 
                                <Loader2 className="animate-spin w-10 h-10 text-flup-violet" />:
                                <p className="text-2xl font-bold text-flup-ink">{ vendorDashboardDetailsResponse?.payload?.totalBookings || 0}</p>

              }

                <p className="text-sm text-flup-ink-3">Total Bookings</p>
              </div>
            </div>
          </div>

          <div className="bg-flup-surface rounded-2xl p-5 shadow-sm border border-flup-hairline hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              {/* <div>
                <p className="text-2xl font-bold text-flup-ink">
                  {activeVenues.filter((v) => v.plan === "elite").length}
                </p>
                <p className="text-sm text-flup-ink-3">Elite Partners</p>
              </div> */}

            <div>
            {vendorDashboardDetailsResponse.isLoading || !vendorDashboardDetailsResponse?.payload ? 
                                <Loader2 className="animate-spin w-10 h-10 text-flup-violet" />:
                <p className="text-2xl font-bold text-flup-ink">
                  {vendorDashboardDetailsResponse?.payload?.revenue || 0}
                </p>}
                <p className="text-sm text-flup-ink-3">Total revenue</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promoted Venues List */}
      <section>
        {/* <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-[19px] font-bold text-flup-ink">All promoted venues</h2>
          <Button variant="outline" size="sm" 
          onClick={()=>{

          }}
          className="text-primary border-primary hover:bg-primary/5 hover:text-green-700">
            + Add Venue
          </Button>
        </div> */}

        <div className="space-y-4">
        {venuesState?.getVendorPromotionsResponse?.payload?.activePromotions?.map((venue,index)=>{
          const dayleft = getDaysLeftFromTodayIST(venue?.promotion?.endDate)
          const expiryStatus = getExpiryStatus(dayleft);

          return (
            <div
            key={index}
            className="bg-flup-surface rounded-2xl shadow-sm border border-flup-hairline overflow-hidden hover:shadow-lg transition-all duration-300 animate-fade-in group max-h-[200px]"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex flex-col md:flex-row">
              {/* Image */}
              <div className="relative w-full md:w-48 h-48 md:h-auto flex-shrink-0">
                <img
                  src={venue?.venue?.venueThumbnail}
                  alt={venue?.venue?.name}
                  
                  className="w-full h-full object-cover group-hover:scale-105 object-cover transition-transform duration-300"
                />
                <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-semibold flex items-center gap-1.5 shadow-lg`}>
                  <Crown className="w-3.5 h-3.5" />
                  {"Premium"}
                </div>
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  {venue?.venue?.averageRating} ({venue?.venue?.totalRatings+40})
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-5">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-flup-ink mb-1 group-hover:text-primary transition-colors">
                      {venue?.venue?.name}
                    </h3>
                    <p className="text-sm text-flup-ink-3 flex items-center gap-1 mb-3">
                      <MapPin className="w-4 h-4" />
                      {venue?.venue?.address?.locality}, {venue?.venue?.address?.city}, {venue?.venue?.address?.state}

                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {venue?.venue?.sportsCategories?.map((sport) => (
                        <Badge
                          key={sport}
                          variant="secondary"
                          className="bg-flup-surface-alt text-flup-ink-2 hover:bg-flup-sunken"
                        >
                          {sport}
                        </Badge>
                      ))}
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-6 text-sm">
                      {/* <div className="flex items-center gap-2 text-flup-ink-2">
                        <Eye className="w-4 h-4 text-flup-info" />
                        <span className="font-medium">{"12,250"}</span>
                        <span className="text-flup-ink-3">views</span>
                      </div> */}
                      <div className="flex items-center gap-2 text-flup-ink-2">
                        <Users className="w-4 h-4 text-green-500" />
                        <span className="font-medium">{ venue?.venue?.totalRatings}</span>
                        <span className="text-flup-ink-3">Rating</span>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Status */}
                  <div className="md:text-right space-y-3">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${expiryStatus.bg}`}>
                      <Clock className={`w-4 h-4 ${expiryStatus.color}`} />
                      <span className={`text-sm font-medium ${expiryStatus.color}`}>
                        {expiryStatus.status}
                      </span>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-flup-ink-2 md:justify-end">
                        <Calendar className="w-4 h-4" />
                        <span>Expires: {new Date(venue?.promotion?.endDate).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs text-flup-ink-3">
                        {dayleft} days remaining
                      </p>
                    </div>

                    <div className="w-full md:w-32">
                      <Progress 
                        value={Math.min((dayleft/ 60) * 100, 100)} 
                        className="h-2"
                      />
                    </div>


                      {dayleft <= 12?

                    <Button
                    size="sm"
                    variant={dayleft <= 7 ? "default" : "outline"}
                    className={dayleft <= 7 ? "bg-primary hover:bg-primary/90" : ""}

                    onClick={()=>RenewNowHandler(venue?.venue?._id)}
                    >
                    Renew Now
                  <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  :
                    <Button
                    onClick={()=>{
                      navigate(`${routes.VenueManagementHome}/${venue?.venue?._id} `)

                    }}
                    size="sm"
                    variant={dayleft <= 7 ? "default" : "outline"}
                    className={dayleft <= 7 ? "bg-primary hover:bg-primary/90" : ""}
                  >
                    {"Manage"}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                                }
                 
                  </div>
                </div>
              </div>
            </div>
          </div>
          )
        })}
      </div>
        {/* <div className="space-y-4">
          {activeVenues
            .sort((a, b) => {
              const planOrder = { elite: 0, premium: 1, basic: 2 };
              return planOrder[a.plan] - planOrder[b.plan];
            })
            .map((venue, index) => {
              const plan = planConfig[venue.plan];
              const PlanIcon = plan.icon;
              const expiryStatus = getExpiryStatus(venue.daysRemaining);

              return (
                <div
                  key={venue.id}
                  className="bg-flup-surface rounded-2xl shadow-sm border border-flup-hairline overflow-hidden hover:shadow-lg transition-all duration-300 animate-fade-in group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="relative w-full md:w-48 h-48 md:h-auto flex-shrink-0">
                      <img
                        src={venue.image}
                        alt={venue.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-full bg-gradient-to-r ${plan.gradient} text-white text-xs font-semibold flex items-center gap-1.5 shadow-lg`}>
                        <PlanIcon className="w-3.5 h-3.5" />
                        {plan.label}
                      </div>
                      <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        {venue.rating} ({venue.reviews})
                      </div>
                    </div>

                    <div className="flex-1 p-5">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-flup-ink mb-1 group-hover:text-primary transition-colors">
                            {venue.name}
                          </h3>
                          <p className="text-sm text-flup-ink-3 flex items-center gap-1 mb-3">
                            <MapPin className="w-4 h-4" />
                            {venue.location}
                          </p>

                          <div className="flex flex-wrap gap-2 mb-4">
                            {venue.sports.map((sport) => (
                              <Badge
                                key={sport}
                                variant="secondary"
                                className="bg-flup-surface-alt text-flup-ink-2 hover:bg-flup-sunken"
                              >
                                {sport}
                              </Badge>
                            ))}
                          </div>

                          <div className="flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-2 text-flup-ink-2">
                              <Eye className="w-4 h-4 text-flup-info" />
                              <span className="font-medium">{venue.views.toLocaleString()}</span>
                              <span className="text-flup-ink-3">views</span>
                            </div>
                            <div className="flex items-center gap-2 text-flup-ink-2">
                              <Users className="w-4 h-4 text-green-500" />
                              <span className="font-medium">{venue.bookings}</span>
                              <span className="text-flup-ink-3">bookings</span>
                            </div>
                          </div>
                        </div>

                        <div className="md:text-right space-y-3">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${expiryStatus.bg}`}>
                            <Clock className={`w-4 h-4 ${expiryStatus.color}`} />
                            <span className={`text-sm font-medium ${expiryStatus.color}`}>
                              {expiryStatus.status}
                            </span>
                          </div>

                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-flup-ink-2 md:justify-end">
                              <Calendar className="w-4 h-4" />
                              <span>Expires: {new Date(venue.expiresAt).toLocaleDateString()}</span>
                            </div>
                            <p className="text-xs text-flup-ink-3">
                              {venue.daysRemaining} days remaining
                            </p>
                          </div>

                          <div className="w-full md:w-32">
                            <Progress 
                              value={Math.min((venue.daysRemaining / 60) * 100, 100)} 
                              className="h-2"
                            />
                          </div>

                          <Button
                            size="sm"
                            variant={venue.daysRemaining <= 7 ? "default" : "outline"}
                            className={venue.daysRemaining <= 7 ? "bg-primary hover:bg-primary/90" : ""}
                          >
                            {venue.daysRemaining <= 7 ? "Renew Now" : "Manage"}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div> */}
      </section>

      {/* Non-Promoted Venues Section */}
      <section>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-flup-ink-2" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-flup-ink">Venues Without Promotion</h2>
              <p className="text-sm text-flup-ink-3">These venues haven't subscribed to promotion yet</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {venuesState?.getVendorPromotionsResponse?.payload?.
          notPromotedVenues && venuesState?.getVendorPromotionsResponse?.payload?.notPromotedVenues.map((venue, index) => (
            <div
              key={venue._id}
              className="bg-flup-surface rounded-2xl shadow-sm border border-dashed border-flup-hairline overflow-hidden hover:shadow-md hover:border-primary/30 transition-all duration-300 animate-fade-in group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex">
                {/* Image */}
                <div className="relative w-32 h-36 flex-shrink-0">
                  <img
                    src={venue.venueThumbnail || Images?.ground}
                    alt={venue.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                  />
                  <div className="absolute inset-0 bg-gray-900/20 group-hover:bg-transparent transition-all duration-300" />
                </div>

                {/* Content */}
                <div className="flex-1 p-4 flex flex-col justify-between">
                  <div>
                    <h3 className="font-semibold text-flup-ink mb-1 group-hover:text-primary transition-colors">
                      {venue.name}
                    </h3>
                    <p className="text-xs text-flup-ink-3 flex items-center gap-1 mb-2">
                      <MapPin className="w-3 h-3" />
                      {venue?.address?.locality},{venue?.address?.city}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-flup-ink-2 mb-2">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      {venue.averageRating} ({venue.totalRatings} reviews)
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {venue?.sportsCategories?.slice(0, 2).map((sport:string) => (
                        <Badge
                          key={sport}
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 border-flup-hairline text-flup-ink-3"
                        >
                          {sport}
                        </Badge>
                      ))}
                      {venue?.sportsCategories?.length > 2 && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-flup-hairline text-flup-ink-3">
                          +{venue.sportsCategories.length - 2}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <ButtonWithLoader
                  isLoading={initPayment && selectedVenueId === venue?._id}
                  onClick={()=>promoteVenueHandler(venue?._id)}
                    size="sm"
                    className="mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs h-8"
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    Promote Venue for ₹30
                  </ButtonWithLoader>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-8 bg-gradient-to-r from-primary/10 via-green-50 to-emerald-50 rounded-2xl p-6 border border-primary/20 animate-fade-in">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-green-600 flex items-center justify-center shadow-lg">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-flup-ink text-lg">Boost Your Venue's Visibility</h3>
                <p className="text-sm text-flup-ink-2">Get 5x more bookings with our promotion plans</p>
              </div>
            </div>

            <Button className="bg-primary hover:bg-primary/90 px-6 rounded-full">
              {/* Just Promote @INR-9  */}
              Boost your visibility and promote your venues at INR-30
              {/* <ChevronRight className="w-4 h-4 ml-1" /> */}
            </Button>
            {/* <Button className="bg-primary hover:bg-primary/90 px-6">
              View Promotion venue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button> */}
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default VenueRecommendation;
